import CantileverButton from 'components/CantileverButton'
import { ChevronLeft, ChevronRight, Ellipsis } from 'lucide-react'
import React from 'react'
import styled, { useTheme } from 'styled-components'

const PaginationButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  padding: 4px;
`

const MAX_PAGE_BUTTONS = 10

const PaginationButton: React.FC<{
  i: number
  content?: React.ReactNode
  tableCurrentPage: number
  tableGetToPage: (i: number) => void
}> = ({ i, content, tableCurrentPage, tableGetToPage }) => {
  const theme = useTheme()
  return (
    <CantileverButton
      square
      ghost={tableCurrentPage !== i}
      textColor={
        tableCurrentPage === i
          ? theme.forecast.defaultText
          : theme.forecast.secondaryText
      }
      onClick={() => tableGetToPage(i)}>
      {content ?? i + 1}
    </CantileverButton>
  )
}

export const Pagination: React.FC<{
  tablePreviousPage: () => void
  tableNextPage: () => void
  getCanNextPage: () => boolean
  getCanPreviousPage: () => boolean
  tablePagesCount: number
  tableGetToPage: (i: number) => void
  tableCurrentPage: number
}> = ({
  tablePreviousPage,
  tableNextPage,
  getCanNextPage,
  getCanPreviousPage,
  tablePagesCount,
  tableGetToPage,
  tableCurrentPage,
}) => {
  const theme = useTheme()

  const paginationButtons = () => {
    const buttons = []

    const maxButtons = MAX_PAGE_BUTTONS
    const totalPages = tablePagesCount
    const currentPage = tableCurrentPage + 1 // 1-based
    const centerIndex = 5 // for maxButtons=10, center at index 5 (6th button)

    // Always show first page
    buttons.push(
      <PaginationButton
        i={0}
        key={0}
        tableCurrentPage={tableCurrentPage}
        tableGetToPage={tableGetToPage}
      />
    )

    if (totalPages <= maxButtons) {
      // Show all pages
      for (let i = 1; i < totalPages - 1; i++) {
        buttons.push(
          <PaginationButton
            i={i}
            key={i}
            tableCurrentPage={tableCurrentPage}
            tableGetToPage={tableGetToPage}
          />
        )
      }
    } else {
      // Left-anchored window
      if (currentPage <= centerIndex + 1) {
        for (let i = 1; i < maxButtons - 2; i++) {
          buttons.push(
            <PaginationButton
              i={i}
              key={i}
              tableCurrentPage={tableCurrentPage}
              tableGetToPage={tableGetToPage}
            />
          )
        }
        // Ellipsis (jump to first truncated page after the last shown)
        buttons.push(
          <PaginationButton
            key={'ellipsis-end'}
            content={
              <Ellipsis width={16} color={theme.forecast.secondaryText} />
            }
            i={maxButtons - 2}
            tableCurrentPage={tableCurrentPage}
            tableGetToPage={tableGetToPage}
          />
        )
      }

      // Right-anchored window
      else if (currentPage >= totalPages - (maxButtons - centerIndex - 1)) {
        // Ellipsis (jump to first truncated page before the first shown)
        buttons.push(
          <PaginationButton
            key={'ellipsis-start'}
            content={
              <Ellipsis width={16} color={theme.forecast.secondaryText} />
            }
            i={totalPages - (maxButtons - 2) - 1}
            tableCurrentPage={tableCurrentPage}
            tableGetToPage={tableGetToPage}
          />
        )
        for (let i = totalPages - (maxButtons - 2); i < totalPages - 1; i++) {
          buttons.push(
            <PaginationButton
              i={i}
              key={i}
              tableCurrentPage={tableCurrentPage}
              tableGetToPage={tableGetToPage}
            />
          )
        }
      }

      // Centered window
      else {
        // Left ellipsis (jump to first truncated page after 1)
        buttons.push(
          <PaginationButton
            key={'ellipsis-start'}
            content={
              <Ellipsis width={16} color={theme.forecast.secondaryText} />
            }
            i={currentPage - centerIndex}
            tableCurrentPage={tableCurrentPage}
            tableGetToPage={tableGetToPage}
          />
        )
        // Middle window
        const start = currentPage - centerIndex + 1
        for (let i = 0; i < maxButtons - 4; i++) {
          const page = start + i
          buttons.push(
            <PaginationButton
              i={page}
              key={page}
              tableCurrentPage={tableCurrentPage}
              tableGetToPage={tableGetToPage}
            />
          )
        }
        // Right ellipsis (jump to first truncated page before N)
        buttons.push(
          <PaginationButton
            key={'ellipsis-end'}
            content={
              <Ellipsis width={16} color={theme.forecast.secondaryText} />
            }
            i={start + maxButtons - 4}
            tableCurrentPage={tableCurrentPage}
            tableGetToPage={tableGetToPage}
          />
        )
      }
    }

    // Always show last page (if not already shown)
    if (totalPages > 1) {
      buttons.push(
        <PaginationButton
          i={totalPages - 1}
          key={totalPages - 1}
          tableCurrentPage={tableCurrentPage}
          tableGetToPage={tableGetToPage}
        />
      )
    }

    return buttons
  }

  return (
    <PaginationButtonsContainer>
      <CantileverButton
        iconOnly
        ghost
        onClick={() => tablePreviousPage()}
        disabled={!getCanPreviousPage()}>
        <ChevronLeft width={20} color={theme.forecast.secondaryText} />
      </CantileverButton>
      {paginationButtons()}
      <CantileverButton
        iconOnly
        ghost
        disabled={!getCanNextPage()}
        onClick={() => tableNextPage()}>
        <ChevronRight width={20} color={theme.forecast.secondaryText} />
      </CantileverButton>
    </PaginationButtonsContainer>
  )
}
